// Full-screen success wordings + CTAs, ported from the monolith. Brand is the
// only axis of variation anticipated for V2; V1 has only `nounou-top`.
// Destinations: the primary CTA navigates to the BACKEND's post-signup redirectUrl at
// runtime (see useSignupFlow#onSuccessPrimary); the `primaryDest` values below are
// static fallbacks for the degraded case and MUST be real Rails routes — they mirror
// Registrations::RedirectPathFor's outputs. Secondary dests are always static and
// must be real routes too. Sub-profiles ("Autre": MAM/RPE/trainer/agency) get a
// single CTA.
export const DEFAULT_BRAND_KEY = 'nounou-top'

export interface SecondaryCta {
  kind: 'link' | 'button'
  label: string
  dest: string
}

interface PathWording {
  incentive: string
  primaryCta: string
  primaryDest: string
  secondary: SecondaryCta | null
}

interface SubProfileWording {
  incentive: string
  primaryCta: string
  primaryDest: string
}

interface BrandWording {
  introStandard: string
  introOther: string
  byPath: Record<string, PathWording>
  bySubProfile: Record<string, SubProfileWording>
}

export interface SuccessConfig {
  intro: string
  incentive: string
  primaryCta: string
  primaryDest: string
  secondary: SecondaryCta | null
}

export const SUCCESS_WORDINGS: Record<string, BrandWording> = {
  'nounou-top': {
    introStandard: 'Et voilà, votre compte Nounou-Top est créé ! 🎉',
    introOther: 'Et voilà, votre compte est créé ! 🎉',
    byPath: {
      marketplace_family: {
        incentive: 'Pour contacter les pros qui vous intéressent, créez votre annonce.',
        primaryCta: 'Créer mon annonce →',
        primaryDest: '/marketplace/listings/child/new',
        secondary: { kind: 'link', label: 'Plus tard', dest: '/' },
      },
      marketplace_childminder: {
        incentive: 'Pour échanger avec les particuliers près de chez vous, créez votre annonce.',
        primaryCta: 'Créer mon annonce →',
        primaryDest: '/marketplace/listings/child/new',
        secondary: { kind: 'link', label: 'Plus tard', dest: '/' },
      },
      management_employer: {
        incentive: 'On démarre par votre premier contrat ?',
        primaryCta: 'Créer mon premier contrat →',
        primaryDest: '/ajouter-un-contrat',
        secondary: { kind: 'button', label: 'Découvrir Gestion PRO', dest: '/gestion' },
      },
      management_employee: {
        incentive: 'Première étape : ajouter votre premier contrat.',
        primaryCta: 'Ajouter mon premier contrat →',
        primaryDest: '/ajouter-un-contrat',
        secondary: { kind: 'button', label: 'Découvrir Gestion PRO', dest: '/gestion' },
      },
      notebook_employer: {
        incentive: 'Pour démarrer, configurez votre espace.',
        primaryCta: 'Configurer mon espace →',
        primaryDest: '/accueil-quotidien',
        secondary: { kind: 'button', label: 'Découvrir le Quotidien', dest: '/suivi-quotidien' },
      },
      notebook_employee: {
        incentive: 'Pour démarrer, configurez votre espace.',
        primaryCta: 'Configurer mon espace →',
        primaryDest: '/accueil-quotidien',
        secondary: { kind: 'button', label: 'Découvrir le Quotidien', dest: '/suivi-quotidien' },
      },
    },
    bySubProfile: {
      mam: { incentive: 'Vous pouvez maintenant gérer votre MAM.', primaryCta: 'Accéder à mon espace →', primaryDest: '/profile/edit_mam' },
      rpe: { incentive: 'Vous pouvez maintenant gérer votre RPE.', primaryCta: 'Accéder à mon espace →', primaryDest: '/accueil-gestion' },
      trainer: { incentive: 'Vous pouvez maintenant gérer votre organisme.', primaryCta: 'Accéder à mon espace →', primaryDest: '/accueil-gestion' },
      agency: { incentive: 'Vous pouvez maintenant gérer votre agence.', primaryCta: 'Accéder à mon espace →', primaryDest: '/listings-home' },
    },
  },
}

// Resolve the success-screen config from the final answers. Sub-profile wins
// (single CTA); otherwise the path config, defaulting to marketplace_family.
export function resolveSuccess(
  { brandKey = DEFAULT_BRAND_KEY, pathKey, subProfile }:
  { brandKey?: string; pathKey?: string | null; subProfile?: string | null } = {},
): SuccessConfig {
  const brand = SUCCESS_WORDINGS[brandKey] ?? SUCCESS_WORDINGS[DEFAULT_BRAND_KEY]!

  if (subProfile && brand.bySubProfile[subProfile]) {
    const c = brand.bySubProfile[subProfile]!
    return {
      intro: brand.introOther,
      incentive: c.incentive,
      primaryCta: c.primaryCta,
      primaryDest: c.primaryDest,
      secondary: null,
    }
  }

  const p = (pathKey && brand.byPath[pathKey]) || brand.byPath.marketplace_family!
  return {
    intro: brand.introStandard,
    incentive: p.incentive,
    primaryCta: p.primaryCta,
    primaryDest: p.primaryDest,
    secondary: p.secondary,
  }
}
